import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { ReceiptFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { IconDefinitionConstants, ReportConstants } from "utils/constants";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  FilterButton,
  DialogActionButton,
  PageLayouts,
  ReloadEvent,
  ViewPanel,
  ChartContextProvider,
  ChartToggle,
  WidgetArea,
  useCheckRelations,
} from "components/common";
import ReceiptListView from "./listView";
import ReceiptMonthlyPurchasesDistributionChart from "./widgets/monthlyPurchasesDistribution";
import ReceiptCategoriesDistributionChart from "./widgets/categoriesDistribution";
import useReceiptReportFilterDefinition from "./forms/useReportFilterDefinition";
import { useCreateDocumentDialog } from "components/documents";
import { Grid } from "@mui/material";
import { useReceiptService } from "serviceHooks";
import useReceiptPermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ReceiptReport: React.FC = (props) => {
  const receiptService = useReceiptService();
  const createReceiptDialog = useCreateDocumentDialog;
  const {
    filterFormFields: reportFilterDefinition,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
    validationSchema,
  } = useReceiptReportFilterDefinition();

  const { getIsReadonlyUser } = useCheckRelations();
  const isReadonlyUser = getIsReadonlyUser();
  const { receiptViewer, hasReceiptChartPermissions } = useReceiptPermissions();

  const tableRef = React.useRef<TableHandles>(null);

  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipts);
  const fileName = `${title} - ${companyName}`;

  const exportReportExcel = async () => {
    tableRef.current?.exportToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportToCSV();
  };

  return (
    <ReloadEvent
      uniqueKey={ReportConstants.ReportKeys.Receipt}
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<ReceiptFormTypes.Filter>
          entityKey={0}
          hasPermissions={receiptViewer}
          title={title}
          topRightButtons={
            <DashboardButtonGroup
              buttons={
                isReadonlyUser
                  ? [<FilterButton />]
                  : [
                      <DialogActionButton reload={reload} actionDialog={createReceiptDialog} size="sm">
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.CreateReceipt)}
                      </DialogActionButton>,
                      ACTION_SEPARATOR,
                      <FilterButton />,
                    ]
              }
              moreActions={[
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportExcel,
                },
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportCSV,
                },
              ]}
            />
          }
          filterFields={reportFilterDefinition}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.Receipt}
          reportToAPIFilterFormatter={receiptService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
          validationSchema={validationSchema}
        >
          {hasReceiptChartPermissions ? (
            <ChartContextProvider>
              <WidgetArea>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.PurchasesPerMonth)}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                  topRightContent={<ChartToggle />}
                >
                  <ReceiptMonthlyPurchasesDistributionChart />
                </ViewPanel>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Receipt.DistributionOfCategories
                  )}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                >
                  <ReceiptCategoriesDistributionChart />
                </ViewPanel>
              </WidgetArea>
            </ChartContextProvider>
          ) : (
            <></>
          )}
          <Grid container item>
            <ReceiptListView reloadKey={reloadKey} ref={tableRef} exportFileName={fileName} />
          </Grid>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default ReceiptReport;
